import { createAction, props } from '@ngrx/store';
import { CustomImage } from '@design/models/custom-image.model';

export namespace fromCustomImageActions {
  // Upload actions
  export const UploadCustomImage = createAction(
    '[Custom Image] Upload Custom Image',
    props<{ file: File; uploadSourceType: 'GoogleMap' | 'GoogleSolar' | 'NoMap' }>()
  );

  export const UploadCustomImageSuccess = createAction(
    '[Custom Image] Upload Custom Image Success',
    props<{ customImage: CustomImage }>()
  );

  export const UploadCustomImageFailure = createAction(
    '[Custom Image] Upload Custom Image Failure',
    props<{ error: string }>()
  );

  // Load actions (for existing images from S3)
  export const LoadCustomImage = createAction(
    '[Custom Image] Load Custom Image',
    props<{ imageUrl: string }>()
  );

  export const LoadCustomImageSuccess = createAction(
    '[Custom Image] Load Custom Image Success',
    props<{ customImage: CustomImage }>()
  );

  // Update actions
  export const UpdateCustomImage = createAction(
    '[Custom Image] Update Custom Image',
    props<{ customImage: CustomImage }>()
  );

  export const UpdateCustomImagePosition = createAction(
    '[Custom Image] Update Custom Image Position',
    props<{ customImage: CustomImage; x: number; y: number; z: number }>()
  );

  export const UpdateCustomImageRotation = createAction(
    '[Custom Image] Update Custom Image Rotation',
    props<{ customImage: CustomImage; rotation: number }>()
  );

  export const UpdateCustomImageOpacity = createAction(
    '[Custom Image] Update Custom Image Opacity',
    props<{ opacity: number }>()
  );

  export const UpdateCustomImageVisibility = createAction(
    '[Custom Image] Update Custom Image Visibility',
    props<{ visible: boolean }>()
  );

  export const UpdateCustomImageScale = createAction(
    '[Custom Image] Update Custom Image Scale',
    props<{ width: number; height: number }>()
  );

  // Calibration actions
  export const BeginCalibration = createAction(
    '[Custom Image] Begin Calibration',
    props<{ customImage: CustomImage }>()
  );

  export const CalibrateCustomImage = createAction(
    '[Custom Image] Calibrate Custom Image',
    props<{
      customImage: CustomImage;
      point1: { x: number; y: number; z: number };
      point2: { x: number; y: number; z: number };
      actualDistance: number;
    }>()
  );

  export const CalibrateCustomImageSuccess = createAction(
    '[Custom Image] Calibrate Custom Image Success',
    props<{ customImage: CustomImage }>()
  );

  export const CancelCalibration = createAction(
    '[Custom Image] Cancel Calibration'
  );

  // Track calibration point placement progress
  export const SetCalibrationPointsCount = createAction(
    '[Custom Image] Set Calibration Points Count',
    props<{ count: number }>()
  );

  // Open calibration dialog (after first point is placed)
  export const OpenCalibrationDialog = createAction(
    '[Custom Image] Open Calibration Dialog',
    props<{ customImage: CustomImage; pointsCount: number }>()
  );

  // Focus the calibration dialog input (when second point is placed)
  export const FocusCalibrationDialogInput = createAction(
    '[Custom Image] Focus Calibration Dialog Input'
  );

  // Delete actions
  export const DeleteCustomImage = createAction(
    '[Custom Image] Delete Custom Image'
  );

  export const DeleteCustomImageSuccess = createAction(
    '[Custom Image] Delete Custom Image Success'
  );

  // Replace actions
  export const ReplaceCustomImage = createAction(
    '[Custom Image] Replace Custom Image',
    props<{ file: File }>()
  );

  // Dialog actions
  export const ShowConfirmationDialog = createAction(
    '[Custom Image] Show Confirmation Dialog'
  );

  export const HideConfirmationDialog = createAction(
    '[Custom Image] Hide Confirmation Dialog'
  );

  export const SetDontShowConfirmationAgain = createAction(
    '[Custom Image] Set Dont Show Confirmation Again',
    props<{ dontShow: boolean }>()
  );

  // Selection action (when custom image icon is clicked)
  export const SelectCustomImageTool = createAction(
    '[Custom Image] Select Custom Image Tool'
  );

  // Edit mode actions (controls dragging/rotation in threejs)
  export const EnterEditMode = createAction(
    '[Custom Image] Enter Edit Mode'
  );

  export const ExitEditMode = createAction(
    '[Custom Image] Exit Edit Mode'
  );

  // Merge custom image with ground image action
  // When exiting edit mode, the custom image is merged with the ground plane texture at the stated opacity
  export const MergeCustomImageWithGround = createAction(
    '[Custom Image] Merge Custom Image With Ground'
  );

  export const MergeCustomImageWithGroundSuccess = createAction(
    '[Custom Image] Merge Custom Image With Ground Success'
  );

  export const MergeCustomImageWithGroundFailure = createAction(
    '[Custom Image] Merge Custom Image With Ground Failure',
    props<{ error: string }>()
  );

  // Save to S3
  export const SaveCustomImageToS3 = createAction(
    '[Custom Image] Save Custom Image To S3',
    props<{ customImage: CustomImage }>()
  );

  export const SaveCustomImageToS3Success = createAction(
    '[Custom Image] Save Custom Image To S3 Success',
    props<{ s3Url: string }>()
  );

  export const SaveCustomImageToS3Failure = createAction(
    '[Custom Image] Save Custom Image To S3 Failure',
    props<{ error: string }>()
  );

  // Reset state
  export const ResetCustomImageState = createAction(
    '[Custom Image] Reset Custom Image State'
  );

  // Clear upload error
  export const ClearUploadError = createAction(
    '[Custom Image] Clear Upload Error'
  );

  // Unmerge custom image from ground - restore original ground and custom image overlay
  export const UnmergeCustomImageFromGround = createAction(
    '[Custom Image] Unmerge Custom Image From Ground'
  );

  export const UnmergeCustomImageFromGroundSuccess = createAction(
    '[Custom Image] Unmerge Custom Image From Ground Success',
    props<{ preEditSnapshot?: CustomImage | null }>()
  );

  export const UnmergeCustomImageFromGroundFailure = createAction(
    '[Custom Image] Unmerge Custom Image From Ground Failure',
    props<{ error: string }>()
  );

  // Cancel edit mode - restore the custom image to its state before edit mode
  // If it's a newly uploaded image (not yet merged), delete it and restore previous state
  // If it's a pre-merged image, restore its position/rotation/opacity to pre-edit values
  export const CancelEditMode = createAction(
    '[Custom Image] Cancel Edit Mode'
  );

  // Store snapshot of custom image state when entering edit mode
  export const StorePreEditSnapshot = createAction(
    '[Custom Image] Store Pre Edit Snapshot',
    props<{ snapshot: CustomImage | null }>()
  );
}
